import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  authoringSections,
  authoringSectionsByKey,
} from "../../data/authoringSections";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LessonAuthoringService from "../../apis/LessonAuthoringService";
import AuthoringHelpPanel from "./AuthoringHelpPanel";

const AuthoringLayout = () => {
  const navigate = useNavigate();
  const { section, type } = useParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const resolvedSection =
    authoringSectionsByKey[section] || authoringSections[0];
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpPanelContent, setHelpPanelContent] = useState({ type: "design" });
  const [isPrimaryNavCollapsed, setIsPrimaryNavCollapsed] = useState(
    resolvedSection?.key === "assets"
  );

  const [lessonCourses, setLessonCourses] = useState([]);
  const [lessonCoursesLoading, setLessonCoursesLoading] = useState(false);
  const [lessonCoursesError, setLessonCoursesError] = useState(null);
  const [selectedLessonCourseId, setSelectedLessonCourseId] = useState(null);
  const [isLessonsNavCollapsible, setIsLessonsNavCollapsible] = useState(false);
  const [isLessonsNavCollapsed, setIsLessonsNavCollapsed] = useState(false);

  const isLessonsSection = resolvedSection?.key === "lessons";

  const sectionTypes = useMemo(() => {
    const types = resolvedSection?.types || [];

    if (resolvedSection?.key !== "taxonomy") {
      return types;
    }

    return [...types].sort((a, b) => {
      if (a.key === "arabic") {
        return -1;
      }

      if (b.key === "arabic") {
        return 1;
      }

      const labelA = a.navLabel || "";
      const labelB = b.navLabel || "";

      return labelA.localeCompare(labelB, undefined, { sensitivity: "base" });
    });
  }, [resolvedSection]);

  const resolvedType =
    sectionTypes.find((candidate) => candidate.key === type) ||
    sectionTypes[0] ||
    {};

  const loadLessonCourses = useCallback(async () => {
    if (!isLessonsSection) {
      return;
    }

    setLessonCoursesLoading(true);
    setLessonCoursesError(null);
    try {
      const response = await LessonAuthoringService.fetchCourses();
      const normalized = Array.isArray(response) ? response : [];
      setLessonCourses(normalized);
      setSelectedLessonCourseId((previous) => {
        if (
          previous !== null &&
          normalized.some(
            (course) =>
              String(course?.id) === String(previous) &&
              Number(course?.roleId) === 2 &&
              course?.enrollmentActiive === true
          )
        ) {
          return previous;
        }

        const defaultCourse = normalized.find(
          (course) =>
            Number(course?.roleId) === 2 && course?.enrollmentActiive === true
        );

        return defaultCourse ? defaultCourse.id : null;
      });
    } catch (error) {
      console.error("Failed to load authoring courses", error);
      setLessonCoursesError(
        error?.message || "Unable to load courses right now. Please try again later."
      );
      setLessonCourses([]);
      setSelectedLessonCourseId(null);
    } finally {
      setLessonCoursesLoading(false);
    }
  }, [isLessonsSection]);

  useEffect(() => {
    if (!isLessonsSection) {
      setLessonCourses([]);
      setLessonCoursesLoading(false);
      setLessonCoursesError(null);
      setSelectedLessonCourseId(null);
      setIsLessonsNavCollapsible(false);
      setIsLessonsNavCollapsed(false);
      return;
    }

    void loadLessonCourses();
  }, [isLessonsSection, loadLessonCourses]);

  const instructorLessonCourses = useMemo(
    () =>
      lessonCourses.filter((course) => {
        const roleId = Number(course?.roleId);
        const isActive = course?.enrollmentActiive === true;
        return roleId === 2 && isActive;
      }),
    [lessonCourses]
  );

  useEffect(() => {
    if (!isLessonsSection) {
      return;
    }

    const isSelectionValid = instructorLessonCourses.some(
      (course) => String(course?.id) === String(selectedLessonCourseId)
    );

    if (selectedLessonCourseId === null && instructorLessonCourses.length > 0) {
      setSelectedLessonCourseId(instructorLessonCourses[0].id);
      return;
    }

    if (!isSelectionValid) {
      setSelectedLessonCourseId(
        instructorLessonCourses.length > 0 ? instructorLessonCourses[0].id : null
      );
    }
  }, [
    instructorLessonCourses,
    isLessonsSection,
    selectedLessonCourseId,
  ]);

  const handleSelectLessonsCourse = useCallback(
    (courseId) => {
      if (!isLessonsSection) {
        return;
      }

      setSelectedLessonCourseId(courseId);
    },
    [isLessonsSection]
  );

  useEffect(() => {
    if (!isLessonsNavCollapsible) {
      setIsLessonsNavCollapsed(false);
    }
  }, [isLessonsNavCollapsible]);

  useEffect(() => {
    setIsPrimaryNavCollapsed(resolvedSection?.key === "assets");
    setHelpPanelContent({ type: "design" });
  }, [resolvedSection?.key]);

  const handleSetLessonsNavCollapsible = useCallback((value) => {
    setIsLessonsNavCollapsible(Boolean(value));
  }, []);

  const handleSetLessonsNavCollapsed = useCallback((value) => {
    setIsLessonsNavCollapsed(Boolean(value));
  }, []);

  const handleCollapseLessonsNav = useCallback(() => {
    setIsLessonsNavCollapsed(true);
  }, []);

  const handleExpandLessonsNav = useCallback(() => {
    setIsLessonsNavCollapsed(false);
  }, []);

  const handleToggleLessonsNavCollapsed = useCallback(() => {
    setIsLessonsNavCollapsed((previous) => !previous);
  }, []);

  const lessonsNavContext = useMemo(() => {
    if (!isLessonsSection) {
      return null;
    }

    return {
      courses: instructorLessonCourses,
      loading: lessonCoursesLoading,
      error: lessonCoursesError,
      selectedCourseId: selectedLessonCourseId,
      selectCourse: handleSelectLessonsCourse,
      refreshCourses: loadLessonCourses,
      isNavigationCollapsed: isLessonsNavCollapsed,
      setNavigationCollapsed: handleSetLessonsNavCollapsed,
      collapseNavigation: handleCollapseLessonsNav,
      expandNavigation: handleExpandLessonsNav,
      toggleNavigationCollapsed: handleToggleLessonsNavCollapsed,
      setNavigationCollapsible: handleSetLessonsNavCollapsible,
      isNavigationCollapsible: isLessonsNavCollapsible,
    };
  }, [
    handleCollapseLessonsNav,
    handleExpandLessonsNav,
    handleSelectLessonsCourse,
    instructorLessonCourses,
    isLessonsSection,
    lessonCoursesError,
    lessonCoursesLoading,
    handleToggleLessonsNavCollapsed,
    handleSetLessonsNavCollapsed,
    handleSetLessonsNavCollapsible,
    loadLessonCourses,
    selectedLessonCourseId,
    isLessonsNavCollapsed,
    isLessonsNavCollapsible,
  ]);

  useEffect(() => {
    setIsHelpOpen(false);
  }, [resolvedSection.key, resolvedType.key]);

  useEffect(() => {
    if (!isDesktop && isHelpOpen) {
      setIsHelpOpen(false);
    }
  }, [isDesktop, isHelpOpen]);

  useEffect(() => {
    if (isDesktop) {
      setIsNavDrawerOpen(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (!resolvedType?.key) {
      return;
    }

    if (section !== resolvedSection.key || type !== resolvedType.key) {
      navigate(`${resolvedSection.key}/${resolvedType.key}`, { replace: true });
    }
  }, [navigate, resolvedSection, resolvedType, section, type]);

  const handleOpenNavigation = () => setIsNavDrawerOpen(true);
  const handleCloseNavigation = () => setIsNavDrawerOpen(false);

  const handleOpenHelpPanel = useCallback(
    (content) => {
      if (content && typeof content === "object") {
        setHelpPanelContent(content);
      } else {
        setHelpPanelContent({ type: "design" });
      }
      setIsHelpOpen(true);
    },
    []
  );

  const handleCloseHelpPanel = useCallback(() => {
    setIsHelpOpen(false);
    setHelpPanelContent({ type: "design" });
  }, []);

  const handleTogglePrimaryNavigation = useCallback(() => {
    setIsPrimaryNavCollapsed((previous) => !previous);
  }, []);

  const navigationContent = (
    <Box
      sx={{
        p: 3,
        height: "100%",
        boxSizing: "border-box",
        overflowY: "auto",
      }}
    >
      <Typography
        id={`authoring-${resolvedSection.key}-heading`}
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 1 }}
      >
        {resolvedSection.label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {resolvedSection.description}
      </Typography>
      {!isLessonsSection && (
        <>
          <Divider sx={{ mb: 2 }} />
          <List sx={{ width: "100%" }}>
            {sectionTypes.map((typeOption) => (
              <ListItemButton
                key={typeOption.key}
                component={NavLink}
                to={`${resolvedSection.key}/${typeOption.key}`}
                onClick={!isDesktop ? handleCloseNavigation : undefined}
                sx={{
                  alignItems: "flex-start",
                  borderRadius: 2,
                  mb: 1,
                  "&.active": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                <ListItemText
                  primary={typeOption.navLabel}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
      {isLessonsSection && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Your Courses
            </Typography>
            <Tooltip title="Refresh courses">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    void loadLessonCourses();
                  }}
                  disabled={lessonCoursesLoading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {lessonCoursesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : lessonCoursesError ? (
            <Alert severity="error">{lessonCoursesError}</Alert>
          ) : instructorLessonCourses.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No instructor courses found.
            </Typography>
          ) : (
            <List dense disablePadding>
              {instructorLessonCourses.map((course) => (
                <ListItemButton
                  key={course.id}
                  onClick={() => {
                    handleSelectLessonsCourse(course.id);
                    if (!isDesktop) {
                      handleCloseNavigation();
                    }
                  }}
                  selected={
                    String(selectedLessonCourseId) === String(course.id)
                  }
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                  }}
                >
                  <ListItemText
                    primary={course.name}
                    secondary={course.description || "No description provided."}
                    primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)" },
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        {isDesktop ? (
          <Box
            component="nav"
            aria-label={`${resolvedSection.label} templates`}
            sx={{
              flexShrink: 0,
              width:
                isLessonsSection && isLessonsNavCollapsible
                  ? isLessonsNavCollapsed
                    ? 64
                    : 320
                  : resolvedSection.key === "assets"
                  ? isPrimaryNavCollapsed
                    ? 48
                    : 320
                  : 320,
              transition: theme.transitions.create("width", {
                duration: theme.transitions.duration.shortest,
              }),
              bgcolor: "background.paper",
              borderRight:
                resolvedSection.key === "assets" && isPrimaryNavCollapsed
                  ? 0
                  : 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isLessonsSection && isLessonsNavCollapsible ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: isLessonsNavCollapsed ? "center" : "flex-end",
                    alignItems: "center",
                    p: isLessonsNavCollapsed ? 1 : 2,
                    pt: isLessonsNavCollapsed ? 1 : 2,
                  }}
                >
                  <Tooltip
                    title={
                      isLessonsNavCollapsed
                        ? "Show course navigation"
                        : "Hide course navigation"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={
                          isLessonsNavCollapsed
                            ? handleExpandLessonsNav
                            : handleCollapseLessonsNav
                        }
                        aria-label={
                          isLessonsNavCollapsed
                            ? "Show course navigation"
                            : "Hide course navigation"
                        }
                      >
                        {isLessonsNavCollapsed ? (
                          <ChevronRightIcon fontSize="small" />
                        ) : (
                          <ChevronLeftIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                {!isLessonsNavCollapsed && (
                  <Box sx={{ flexGrow: 1, minHeight: 0 }}>{navigationContent}</Box>
                )}
              </>
            ) : (
              <>
                {resolvedSection.key === "assets" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: isPrimaryNavCollapsed
                        ? "center"
                        : "flex-end",
                      alignItems: "center",
                      p: isPrimaryNavCollapsed ? 1 : 2,
                    }}
                  >
                    <Tooltip
                      title={
                        isPrimaryNavCollapsed
                          ? "Show navigation"
                          : "Hide navigation"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleTogglePrimaryNavigation}
                          aria-label={
                            isPrimaryNavCollapsed
                              ? "Show navigation"
                              : "Hide navigation"
                          }
                        >
                          {isPrimaryNavCollapsed ? (
                            <ChevronRightIcon fontSize="small" />
                          ) : (
                            <ChevronLeftIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                {!isPrimaryNavCollapsed && (
                  <Box sx={{ flexGrow: 1, minHeight: 0 }}>{navigationContent}</Box>
                )}
              </>
            )}
          </Box>
        ) : (
          <Drawer
            variant="temporary"
            open={isNavDrawerOpen}
            onClose={handleCloseNavigation}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: 280,
              },
            }}
          >
            {navigationContent}
          </Drawer>
        )}

        <Box
          component="section"
          id={`authoring-${resolvedSection.key}-panel`}
          aria-labelledby={`authoring-${resolvedSection.key}-heading`}
          sx={{
            flexGrow: 1,
            p: { xs: 3, md: 5 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflowY: "auto",
          }}
        >
          <Outlet
            context={{
              currentSection: resolvedSection,
              currentType: resolvedType,
              openHelpPanel: handleOpenHelpPanel,
              closeHelpPanel: handleCloseHelpPanel,
              toggleHelpPanel: () =>
                setIsHelpOpen((prev) => {
                  const next = !prev;
                  if (!next) {
                    setHelpPanelContent({ type: "design" });
                  }
                  return next;
                }),
              isHelpPanelOpen: isHelpOpen,
              openNavigationPanel: handleOpenNavigation,
              closeNavigationPanel: handleCloseNavigation,
              isNavigationPanelAvailable:
                !isDesktop || resolvedSection.key === "assets",
              lessonsNav: lessonsNavContext,
            }}
          />
        </Box>

        <AuthoringHelpPanel
          open={isHelpOpen}
          onClose={handleCloseHelpPanel}
          currentType={resolvedType}
          content={helpPanelContent}
        />
      </Box>
    </Box>
  );
};

export default AuthoringLayout;
